import { TimeEventType } from "@/models/time-control/timeStateEvents";
import { useCurrTimeState, useIsLocked, useTimeService } from "@/models/time-control/timeStateHooks";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useEffect, useRef } from "react";
import { WindowKind, instanceId, useDesktop } from "../window";

import { datetime_round_default } from "@/time";
import { classNames } from "@/utility/jsx";
import { DateTime } from "luxon";
import { DATE_FORMAT_WITH_WEEKDAY, TIME_FORMAT, normalizeDateString } from "./formats";

import SvgBackIcon from "./icons/back.module.svg";
import SvgClockIcon from "./icons/clock_hour_hand.module.svg";
import SvgMinuteHandIcon from "./icons/clock_minute_hand.module.svg";

import { TimeStates } from "@/models/time-control/timeStateTypes";
import { timezones } from "@/overlay/components/DatetimeSelectionWindow/TimeSettings/TimeZoneConst";
import { getTimeStateIcon } from "@/overlay/components/TimeSettingsButtons/utils";
import type { GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import style from "./style.module.scss";

type TimeSettingsButtonsProps = {
  visibleTime?: DateTime;
};

export const TimeSettingsButtons = ({ visibleTime }: TimeSettingsButtonsProps) => {
  const timeState = useCurrTimeState();
  const desktop = useDesktop();
  const { send } = useTimeService();
  const { i18n } = useLingui();
  const prevVisibleWindowIds = useRef<Set<string>>(new Set());
  const datetimeWindowId = instanceId(WindowKind.DatetimeSelection);

  const { timezone, nextDisplayTime_ } = timeState.context;
  const timeZoneLabel = timezones[timezone as GuiTimeZone]();
  const shownDisplayTime = visibleTime ? visibleTime : nextDisplayTime_;

  const isAtAutoRefreshState = timeState.matches(TimeStates.AUTO_REFRESH);
  const isAtAnimationState = timeState.matches(TimeStates.ANIMATION);
  const isLocked = useIsLocked();

  useEffect(() => {
    const currentlyVisible = new Set(desktop.windowOrder().filter((windowId) => desktop.isWindowVisible(windowId)));

    if (!currentlyVisible.has(datetimeWindowId)) {
      prevVisibleWindowIds.current = currentlyVisible;
      return;
    }

    const hadOtherVisible = Array.from(prevVisibleWindowIds.current).some((id) => id !== datetimeWindowId);
    const hasOtherVisibleNow = Array.from(currentlyVisible).some((id) => id !== datetimeWindowId);

    if (hasOtherVisibleNow && !hadOtherVisible) {
      desktop.dispatch({
        type: "setWindowVisibility",
        windowId: datetimeWindowId,
        isVisible: false,
      });
    }
    prevVisibleWindowIds.current = currentlyVisible;
  }, [desktop, datetimeWindowId]);

  const handleClickCalendarButton = () => {
    desktop.dispatch({ type: "toggleWindowVisibility", windowId: instanceId(WindowKind.DatetimeSelection) });

    if (isAtAnimationState) {
      send({
        type: TimeEventType.updateAnimationMode,
        modeOpts: {
          ...timeState.context.modeOpts,
          isPaused: true,
        },
      });
    }
  };

  function setCurrentDisplayTimeToNow() {
    // Sets the date selection to now in the GUI timezone.
    // Only for the Time Point selection.
    const nowInGuiTimeZone = datetime_round_default(DateTime.utc().setZone(timezone));
    if (!isAtAnimationState) {
      send([{ type: TimeEventType.updateNextDisplayTime, nextDisplayTime_: nowInGuiTimeZone }]);
    }
  }

  function switchTimeNavigationMode() {
    if (isAtAnimationState) {
      send({ type: TimeEventType.setAutoRefreshMode });
      // Close the time window if it's open during the animation mode
      desktop.dispatch({
        type: "setWindowVisibility",
        isVisible: false,
        windowId: instanceId(WindowKind.DatetimeSelection),
      });
    } else {
      send({ type: TimeEventType.setAnimationMode });
    }
  }

  if (!shownDisplayTime) {
    // This should never happen with new time state machine!
    return <></>;
  }

  const localizedDateTime = shownDisplayTime.setZone(timezone).setLocale(i18n.locale);

  return (
    <div className={style["time-settings-button-container"]}>
      {isAtAutoRefreshState ? (
        <mm-tooltip-wrapper tail={true} position="top-right" tooltip-text={t`Set present time`}>
          <button
            data-testid="present-time-btn"
            className={style["btn-left"]}
            type="button"
            onClick={setCurrentDisplayTimeToNow}
          >
            <div className={style["time-settings-clock-icon"]}>
              <SvgClockIcon />
              <SvgMinuteHandIcon />
            </div>
          </button>
        </mm-tooltip-wrapper>
      ) : (
        <></>
      )}
      <mm-tooltip-wrapper tail={true} position="top" tooltip-text={t`Time Settings`}>
        <button
          data-testid="animation-calendar-btn"
          className={classNames(style["btn-center"], isAtAnimationState ? style["btn-left"] : "")}
          type="button"
          onClick={handleClickCalendarButton}
        >
          <span>{normalizeDateString(localizedDateTime.toLocaleString(DATE_FORMAT_WITH_WEEKDAY))}</span>
          <span
            className={classNames(
              "material-icons-round",
              isAtAutoRefreshState && !isLocked && !(timeState.context.modeOpts.refreshRate === 0)
                ? style["time-settings-icon--live"]
                : style["time-settings-icon--calendar"],
            )}
          >
            {getTimeStateIcon(timeState) || "schedule"}
          </span>
          <span>{localizedDateTime.toLocaleString(TIME_FORMAT)}</span>
          <span>{timeZoneLabel}</span>
        </button>
      </mm-tooltip-wrapper>
      {
        <mm-tooltip-wrapper
          tail={true}
          position="top"
          tooltip-text={isAtAnimationState ? t`Exit Animation Mode` : t`Animation Mode`}
        >
          <button
            className={style["btn-right"]}
            type="button"
            onClick={() => {
              switchTimeNavigationMode();
            }}
          >
            {isAtAnimationState ? (
              <SvgBackIcon />
            ) : (
              <span className={classNames("material-icons-round", style["time-settings-icon"])}>
                play_circle_outline
              </span>
            )}
          </button>
        </mm-tooltip-wrapper>
      }
    </div>
  );
};
